import React, { useEffect, useContext, useState, Suspense } from 'react';
import { bool, node, string } from 'prop-types';
import { Card, CardContent } from '@thd-olt-component-react/core-ui';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import {
  alias,
  arrayOf as arrayType,
  bool as boolType,
  number as numberType,
  params,
  shape as shapeType,
  string as stringType
} from '@thd-nucleus/data-sources';
import OrderSampleContent from './OrderSampleContent';
import { useOrderSample } from '../hooks/useOrderSample';
import './order-sample.scss';
import { publish } from '../analytics';
// eslint-disable-next-line max-len
const SampleOverlay = React.lazy(() => import(/* webpackChunkName: "order-sample-overlay" */ './SampleOverlay/SampleOverlay'));
export const OrderSample = ({
  children,
  itemId,
  link,
  oosMessage,
  sampleLimit
}) => {
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('order-sample.ready'); }, []);
  const state = {
    sampleOverlayRequested: false,
    gettingSampleData: false,
    samplesOutOfStock: false,
    loadingSample: false,
  };
  const [orderSampleState, setOrderSampleState] = useState(state);
  const {
    sampleOverlayRequested,
    gettingSampleData,
    samplesOutOfStock,
    loadingSample,
  } = orderSampleState;
  const experienceContext = useContext(ExperienceContext);
  const {
    channel
  } = experienceContext;

  const { storeId, storeZip: zipCode } = useStore();
  const { data, loading: dataLoading } = useOrderSample({ itemId, storeId, zipCode });

  if (!data?.product || dataLoading || !data?.hasSample) {
    return null;
  }

  const {
    price,
    dimensionText,
    sampleId,
    isSuperSku,
    parentId
  } = data;

  const checkSamplesAvailable = ({ loading, orderSamples, oosLoading }) => {
    const samplesAvailable = (orderSamples || []).length;
    const inStock = !!orderSamples?.find((sample) => sample.inStockStatus);
    const notDiscontinued = !!orderSamples?.find((sample) => !sample.discontinued);

    const gettingSamples = loading || !samplesAvailable;

    const outOfStockSamples = !!(samplesAvailable && !loading && (!inStock || !notDiscontinued));

    setOrderSampleState((prevState) => {
      return {
        ...prevState,
        gettingSampleData: gettingSamples,
        samplesOutOfStock: outOfStockSamples,
        loadingSample: oosLoading
      };
    });
  };

  const handleClick = async () => {
    if (isSuperSku) {
      setOrderSampleState((prevState) => {
        return {
          ...prevState,
          gettingSampleData: true,
          sampleOverlayRequested: true,
          loadingSample: true,
        };
      });
    } else {
      const cartReqParams = {
        itemId: sampleId,
        quantity: 1,
        fulfillmentMethod: 'ShipToHome',
        fulfillmentLocation: zipCode
      };

      const cartOptions = {
        channel,
        host: window.location.origin.includes('localhost') ? 'https://www.homedepot.com' : window.location.origin,
        misship: false,
        paypal: false
      };

      window.LIFE_CYCLE_EVENT_BUS.trigger('cart.add-to-cart', {
        cartReqParams,
        cartOptions,
      });
    }
    publish('order-sample.click');
  };

  const sampleOverlayClose = () => {
    setOrderSampleState((prevState) => {
      return {
        ...prevState,
        sampleOverlayRequested: false,
      };
    });
  };
  const shouldOverlayOpen = (sampleOverlayRequested && !gettingSampleData && !samplesOutOfStock) || link;

  return (
    <>
      <Card>
        <CardContent className="card-content--no-padding">
          <OrderSampleContent
            channel={channel}
            dimensionText={dimensionText}
            handleClick={handleClick}
            link={link}
            loading={gettingSampleData}
            loadingSample={loadingSample}
            multipleSamples={isSuperSku}
            oosMessage={oosMessage}
            price={price}
            samplesOOS={samplesOutOfStock}
          >
            {children}
          </OrderSampleContent>
        </CardContent>
      </Card>
      {sampleOverlayRequested && (
        <Suspense fallback={<div>Loading...</div>}>
          <SampleOverlay
            anchorSample={sampleId}
            channel={channel}
            dimensionText={dimensionText}
            onClose={sampleOverlayClose}
            onSampleData={checkSamplesAvailable}
            oosMessage={oosMessage}
            open={shouldOverlayOpen}
            parentId={parentId}
            price={price}
            sampleLimit={sampleLimit}
            storeId={storeId}
            zipCode={zipCode}
          />
        </Suspense>
      )}
    </>
  );
};

OrderSample.displayName = 'OrderSample';

const productModel = {
  dataSources: stringType(),
  identifiers: shapeType({
    itemId: stringType(),
    isSuperSku: boolType(),
    parentId: stringType(),
    sampleId: stringType()
  })
};

const sampleModel = {
  availabilityType: shapeType({
    discontinued: boolType()
  }),
  identifiers: shapeType({
    itemId: stringType()
  }),
  fulfillment: params({ storeId: stringType(), zipCode: stringType() }).shape({
    // TODO: this is being used as a boolean flag, should probably be checking "fulfillable" - see SampleOverlay
    fulfillmentOptions: arrayType(shapeType({
      fulfillable: boolType()
    }))
  }),
  media: shapeType({
    images: arrayType(shapeType({
      sizes: arrayType(stringType()),
      subType: stringType(),
      url: stringType()
    }))
  }),
  pricing: params({ storeId: stringType() }).shape({
    value: numberType({ float: true })
  }),
  specificationGroup: arrayType(shapeType({
    specifications: arrayType(shapeType({
      specName: stringType(),
      specValue: stringType()
    })),
    specTitle: stringType()
  }))
};

OrderSample.dataModel = {
  clientOnlyProduct: alias('product')
    .params({ itemId: stringType().isRequired(), dataSource: stringType() })
    .shape(productModel),
  products: params({ itemIds: arrayType(stringType().isRequired()).isRequired() }).arrayOf(productModel),
  sampleProduct: alias('product')
    .params({ itemId: stringType().isRequired(), dataSource: stringType() })
    .shape(sampleModel),
  sampleProducts: alias('products')
    .params({ itemIds: arrayType(stringType().isRequired()).isRequired() })
    .arrayOf(sampleModel),
  sampleProductsMetadata: alias('metadata').params({ parentId: stringType().isRequired() }).shape({
    attributes: arrayType(shapeType({
      attributeName: stringType()
    })),
    childItemsLookup: arrayType(shapeType({
      itemId: stringType()
    }))
  })
};

OrderSample.propTypes = {
  children: node,
  itemId: string.isRequired,
  link: bool,
  oosMessage: bool,
  sampleLimit: string,
};

OrderSample.defaultProps = {
  children: 'Need a closer look?',
  link: false,
  // API has a hard 16 limit for product queries.
  // Sample Limit should be turned on from a FS and removed from this hard-coded logic
  oosMessage: false,
  sampleLimit: '16'
};
